import { httpClient } from "../../../lib/http/axios";
import type { ApiEnvelope } from "../../../lib/http/contracts";
import {
  mapMarketRegion,
  mapMarketRegionListPage,
  mapMarketRegionMember,
  mapMarketRegionOrganizationOption,
  mapMarketRegionOrganizationUnitOption,
  mapMarketRegionScope,
  mapStaffOption,
  type MarketRegionListPageRaw,
  type MarketRegionMemberRaw,
  type MarketRegionOrganizationListPayloadRaw,
  type MarketRegionOrganizationUnitRaw,
  type MarketRegionRaw,
  type MarketRegionScopeRaw,
  type SysStaffListPayloadRaw,
} from "../api/adapters";
import type {
  MarketRegionItem,
  MarketRegionListPage,
  MarketRegionListParams,
  MarketRegionMemberItem,
  MarketRegionMemberMutation,
  MarketRegionMutation,
  MarketRegionOrganizationOption,
  MarketRegionOrganizationUnitOption,
  MarketRegionScopeItem,
  MarketRegionScopeMutation,
  MarketRegionStaffOption,
} from "../types";

/** 分页读取区域经营配置列表。 */
export async function fetchCrmMarketRegions(params: MarketRegionListParams): Promise<MarketRegionListPage> {
  const response = await httpClient.get<ApiEnvelope<MarketRegionListPageRaw>>("/crm/market-regions", {
    params: {
      page: params.page,
      page_size: params.pageSize,
      keyword: params.keyword,
      region_type: params.regionType,
      is_disabled: params.isDisabled,
    },
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("区域经营配置接口返回了无效数据");
  }
  return mapMarketRegionListPage(response.data.data);
}

/** 创建区域经营配置。 */
export async function createCrmMarketRegion(payload: MarketRegionMutation): Promise<MarketRegionItem> {
  const response = await httpClient.post<ApiEnvelope<MarketRegionRaw>>("/crm/market-regions", toRegionBody(payload));
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("创建区域接口返回了无效数据");
  }
  return mapMarketRegion(response.data.data);
}

/** 更新区域经营配置。 */
export async function updateCrmMarketRegion(
  regionId: number,
  payload: MarketRegionMutation,
): Promise<MarketRegionItem> {
  const response = await httpClient.put<ApiEnvelope<MarketRegionRaw>>(
    `/crm/market-regions/${regionId}`,
    toRegionBody(payload),
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("更新区域接口返回了无效数据");
  }
  return mapMarketRegion(response.data.data);
}

/** 启用或停用区域经营配置。 */
export async function updateCrmMarketRegionStatus(
  regionId: number,
  payload: { isDisabled: boolean; currentUserId: number },
): Promise<MarketRegionItem> {
  const response = await httpClient.put<ApiEnvelope<MarketRegionRaw>>(`/crm/market-regions/${regionId}/status`, {
    is_disabled: payload.isDisabled,
    current_user_id: payload.currentUserId,
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("更新区域状态接口返回了无效数据");
  }
  return mapMarketRegion(response.data.data);
}

/** 读取指定区域的覆盖范围。 */
export async function fetchCrmMarketRegionScopes(regionId: number): Promise<MarketRegionScopeItem[]> {
  const response = await httpClient.get<ApiEnvelope<{ items: MarketRegionScopeRaw[] }>>(
    `/crm/market-regions/${regionId}/scopes`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("区域范围接口返回了无效数据");
  }
  return response.data.data.items.map(mapMarketRegionScope);
}

/** 创建指定区域的覆盖范围。 */
export async function createCrmMarketRegionScope(
  regionId: number,
  payload: MarketRegionScopeMutation,
): Promise<MarketRegionScopeItem> {
  const response = await httpClient.post<ApiEnvelope<MarketRegionScopeRaw>>(
    `/crm/market-regions/${regionId}/scopes`,
    {
      scope_type: payload.scopeType,
      country: payload.country,
      province: payload.province,
      city: payload.city,
      org_id: payload.orgId,
      org_unit_id: payload.orgUnitId,
      customer_id: payload.customerId,
      contact_id: payload.contactId,
      scope_name_snapshot: payload.scopeNameSnapshot,
      is_excluded: payload.isExcluded,
      current_user_id: payload.currentUserId,
      remark: payload.remark,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("创建区域范围接口返回了无效数据");
  }
  return mapMarketRegionScope(response.data.data);
}

/** 删除指定区域的覆盖范围。 */
export async function deleteCrmMarketRegionScope(regionId: number, scopeId: number): Promise<void> {
  await httpClient.delete(`/crm/market-regions/${regionId}/scopes/${scopeId}`);
}

/** 删除指定区域的成员。 */
export async function deleteCrmMarketRegionMember(regionId: number, memberId: number): Promise<void> {
  await httpClient.delete(`/crm/market-regions/${regionId}/members/${memberId}`);
}

/** 读取指定区域的成员列表。 */
export async function fetchCrmMarketRegionMembers(regionId: number): Promise<MarketRegionMemberItem[]> {
  const response = await httpClient.get<ApiEnvelope<{ items: MarketRegionMemberRaw[] }>>(
    `/crm/market-regions/${regionId}/members`,
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("区域成员接口返回了无效数据");
  }
  return response.data.data.items.map(mapMarketRegionMember);
}

/** 创建或更新指定区域的成员。 */
export async function createCrmMarketRegionMember(
  regionId: number,
  payload: MarketRegionMemberMutation,
): Promise<MarketRegionMemberItem> {
  const response = await httpClient.post<ApiEnvelope<MarketRegionMemberRaw>>(
    `/crm/market-regions/${regionId}/members`,
    {
      user_id: payload.userId,
      member_role: payload.memberRole,
      can_assign: payload.canAssign,
      can_edit_scope: payload.canEditScope,
      can_view_stats: payload.canViewStats,
      is_disabled: payload.isDisabled,
      current_user_id: payload.currentUserId,
      remark: payload.remark,
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("保存区域成员接口返回了无效数据");
  }
  return mapMarketRegionMember(response.data.data);
}

/** 读取可配置为区域成员的员工候选。 */
export async function fetchCrmMarketRegionStaffOptions(): Promise<MarketRegionStaffOption[]> {
  const response = await httpClient.get<ApiEnvelope<SysStaffListPayloadRaw>>("/sys/dept-users/staff", {
    params: {
      is_staff: true,
      page: 1,
      page_size: 100,
      sort_field: "update_time",
      sort_direction: "desc",
    },
  });
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("员工候选接口返回了无效数据");
  }
  return (response.data.data.items ?? []).map(mapStaffOption);
}

/** 按省市读取学校范围候选。 */
export async function fetchCrmMarketRegionOrganizationOptions({
  province,
  city,
  keyword,
  limit = 50,
}: {
  province?: string;
  city?: string;
  keyword?: string;
  limit?: number;
}): Promise<MarketRegionOrganizationOption[]> {
  const response = await httpClient.get<ApiEnvelope<MarketRegionOrganizationListPayloadRaw>>(
    "/crm/organizations",
    {
      params: {
        page: 1,
        page_size: limit,
        keyword,
        org_type: "高校",
        province,
        city,
        is_disabled: false,
        sort_field: "org_name",
        sort_direction: "asc",
      },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("学校候选接口返回了无效数据");
  }
  return (response.data.data.items ?? []).map(mapMarketRegionOrganizationOption);
}

/** 按学校读取院系专业范围候选。 */
export async function fetchCrmMarketRegionOrganizationUnitOptions({
  orgId,
  keyword,
  limit = 100,
}: {
  orgId: number;
  keyword?: string;
  limit?: number;
}): Promise<MarketRegionOrganizationUnitOption[]> {
  const response = await httpClient.get<ApiEnvelope<MarketRegionOrganizationUnitRaw[]>>(
    `/crm/contact-workbench/organizations/${orgId}/units`,
    {
      params: { keyword, limit },
    },
  );
  if (response.data.code !== "OK" || !response.data.data) {
    throw new Error("院系专业候选接口返回了无效数据");
  }
  return response.data.data.map(mapMarketRegionOrganizationUnitOption);
}

/** 将前端区域表单转换为后端保存请求体。 */
function toRegionBody(payload: MarketRegionMutation) {
  return {
    region_name: payload.regionName,
    region_code: payload.regionCode,
    region_type: payload.regionType,
    description: payload.description,
    is_disabled: payload.isDisabled,
    current_user_id: payload.currentUserId,
    remark: payload.remark,
  };
}
